import { Monitor01 } from '@untitledui/icons';
import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms';
import { apiPost } from '@/lib/http/client';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const WINDOWS_FEATURES = [
  {
    title: 'No bots in your meetings',
    body: 'Ahoy Desktop captures audio locally – your participants never see a bot join.',
  },
  {
    title: 'Notes arrive automatically',
    body: 'When the meeting ends, a summary appears in Ahoy within minutes.',
  },
  {
    title: 'Works with any meeting tool',
    body: 'Zoom, Teams, Google Meet – if your mic hears it, Ahoy notes it.',
  },
] as const;

const WINDOWS_FEATURES_TITLE_ID = 'desktop-windows-features-title';

export function WindowsComingLater() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email || status === 'submitting') {
        return;
      }
      setStatus('submitting');
      setErrorMessage('');
      try {
        await apiPost('/api/waitlist/join/', { email });
        setStatus('success');
      } catch {
        setStatus('error');
        setErrorMessage(
          'Something went wrong. Try again, and get in touch if it keeps happening.'
        );
      }
    },
    [email, status]
  );

  return (
    <div className='flex flex-col items-center gap-6 text-center'>
      <div className='flex flex-col gap-2'>
        <h2 className='text-2xl font-semibold text-[var(--color-text-primary)]'>
          Coming to Windows
        </h2>
        <p className='max-w-md text-[var(--color-text-secondary)]'>
          Ahoy Desktop is built for Mac today. We are working on Windows – drop
          your email and we will let you know when it ships.
        </p>
      </div>

      <div
        aria-label='Ahoy Desktop preview'
        className='flex w-full flex-col items-center gap-3 rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-6'
        data-testid='windows-visual-preview'
      >
        <Monitor01
          aria-hidden='true'
          className='text-fg-brand-primary'
          height={56}
          width={56}
        />
        <p className='text-sm text-[var(--color-text-secondary)]'>
          Ahoy Desktop for Windows – coming soon
        </p>
      </div>

      <section
        aria-labelledby={WINDOWS_FEATURES_TITLE_ID}
        className='flex w-full flex-col gap-4 text-left'
      >
        <h2
          className='text-lg font-semibold text-[var(--color-text-primary)]'
          id={WINDOWS_FEATURES_TITLE_ID}
        >
          What to expect
        </h2>
        <ul className='flex flex-col gap-3'>
          {WINDOWS_FEATURES.map(({ title, body }) => (
            <li className='flex items-start gap-2' key={title}>
              <span className='mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--color-brand-600)]' />
              <div className='text-sm'>
                <p className='font-medium text-[var(--color-text-primary)]'>
                  {title}
                </p>
                <p className='text-[var(--color-text-secondary)]'>{body}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {status === 'success' ? (
        <p
          className='text-sm font-medium text-[var(--color-text-primary)]'
          role='status'
        >
          Thanks – we will be in touch.
        </p>
      ) : (
        <form
          className='flex w-full max-w-sm flex-col gap-3'
          onSubmit={handleSubmit}
        >
          <Input
            isDisabled={status === 'submitting'}
            isInvalid={status === 'error'}
            isRequired
            label='Email address'
            onChange={handleEmailChange}
            placeholder='you@company.com'
            type='email'
            value={email}
          />
          {status === 'error' && (
            <p
              className='text-sm text-[var(--color-text-error-primary)]'
              role='alert'
            >
              {errorMessage}
            </p>
          )}
          <Button
            color='primary'
            isDisabled={!email || status === 'submitting'}
            isLoading={status === 'submitting'}
            type='submit'
          >
            Notify me
          </Button>
        </form>
      )}
    </div>
  );
}
